import { parseItemDate } from './dateUtils';

/**
 * insightEngine — pure functions that compute pattern insights from raw data arrays.
 *
 * No hooks, no Firestore — purely functional so each insight is unit-testable
 * with plain arrays. All Firestore access lives in usePatternData.
 *
 * Each insight: { icon, title, body, trend, sentiment }
 *   trend:     'up' | 'down' | 'neutral'  (for the arrow indicator)
 *   sentiment: 'positive' | 'negative' | 'neutral'  (for the colour)
 */

const WEEK_MS = 7 * 24 * 60 * 60 * 1000; // data window matches usePatternData (7 days)
const MIN_ENTRIES = 3;

// ── Helpers ────────────────────────────────────────────────────────────────

/** Duration of a sleep item in minutes, or null if it can't be determined. */
export function sleepDurationMinutes(item) {
  if (item.duration != null) return parseFloat(item.duration);
  if (item.startTime && item.endTime) {
    const start = new Date(`2000-01-01T${item.startTime}`);
    const end   = new Date(`2000-01-01T${item.endTime}`);
    let diff = (end - start) / 60000;
    if (diff < 0) diff += 24 * 60; // handle midnight crossover
    if (diff > 0 && diff < 24 * 60) return diff;
  }
  return null;
}

/** True if a sleep session started during night hours (20:00–06:00). */
export function isNightSleep(item) {
  const hour = item.startTime
    ? parseInt(item.startTime.split(':')[0], 10)
    : (parseItemDate(item)?.getHours() ?? 12);
  return hour >= 20 || hour < 6;
}

/**
 * Splits items into this-week (0–7 days ago) and last-week (7–14 days ago)
 * buckets using parseItemDate for the timestamp.
 */
export function splitByWeek(items) {
  const now = Date.now();
  const thisWeek = [];
  const lastWeek = [];
  for (const item of items) {
    const d = parseItemDate(item)?.getTime();
    if (!d) continue;
    const age = now - d;
    if (age < WEEK_MS)         thisWeek.push(item);
    else if (age < 2 * WEEK_MS) lastWeek.push(item);
  }
  return { thisWeek, lastWeek };
}

function mean(arr) {
  if (!arr.length) return null;
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

function formatMinutes(min) {
  if (min == null) return '—';
  const m = Math.round(min);
  if (m < 60) return `${m}m`;
  const h   = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

/** 'up' | 'down' | 'neutral' — neutral if within 5% of last week. */
function trendOf(thisVal, lastVal) {
  if (thisVal == null || lastVal == null || lastVal === 0) return 'neutral';
  const pct = (thisVal - lastVal) / lastVal;
  if (pct >  0.05) return 'up';
  if (pct < -0.05) return 'down';
  return 'neutral';
}

function changeLabel(trend) {
  if (trend === 'up')   return 'up';
  if (trend === 'down') return 'down';
  return 'similar';
}

// ── Individual insight computers ───────────────────────────────────────────

export function feedingIntervalInsight(bottle, breastfeeding) {
  const { thisWeek: tw, lastWeek: lw } = splitByWeek([...bottle, ...breastfeeding]);

  function avgInterval(items) {
    const sorted = items
      .map((i) => parseItemDate(i)?.getTime())
      .filter(Boolean)
      .sort((a, b) => a - b);
    const gaps = [];
    for (let i = 1; i < sorted.length; i++) {
      const gap = (sorted[i] - sorted[i - 1]) / 60000;
      if (gap < 480) gaps.push(gap); // ignore overnight gaps > 8 h
    }
    return gaps.length >= MIN_ENTRIES ? mean(gaps) : null;
  }

  const thisAvg = avgInterval(tw);
  if (thisAvg == null) return null;

  const lastAvg = avgInterval(lw);
  const trend   = trendOf(thisAvg, lastAvg);

  const body = lastAvg != null
    ? `Feeding every ${formatMinutes(thisAvg)} on average — ${changeLabel(trend)} from ${formatMinutes(lastAvg)} last week`
    : `Feeding every ${formatMinutes(thisAvg)} on average this week`;

  // Longer interval = positive (baby spacing out feeds)
  return {
    icon: '🍼',
    title: 'Feeding interval',
    body,
    trend,
    sentiment: trend === 'up' ? 'positive' : trend === 'down' ? 'negative' : 'neutral',
  };
}

export function dailyFeedCountInsight(bottle, breastfeeding) {
  const { thisWeek: tw, lastWeek: lw } = splitByWeek([...bottle, ...breastfeeding]);
  if (tw.length < MIN_ENTRIES) return null;

  function perDay(items) {
    const days = {};
    for (const item of items) {
      const d = parseItemDate(item);
      if (!d) continue;
      const key = d.toDateString();
      days[key] = (days[key] || 0) + 1;
    }
    const counts = Object.values(days);
    return counts.length ? mean(counts) : null;
  }

  const thisAvg = perDay(tw);
  if (thisAvg == null) return null;

  const lastAvg = perDay(lw);
  const trend   = trendOf(thisAvg, lastAvg);

  const body = lastAvg != null
    ? `Averaging ${thisAvg.toFixed(1)} feeds/day — ${changeLabel(trend)} from ${lastAvg.toFixed(1)}/day last week`
    : `Averaging ${thisAvg.toFixed(1)} feeds per day this week`;

  // Direction is age-dependent — treat as neutral to avoid misleading parents
  return { icon: '📊', title: 'Daily feed count', body, trend, sentiment: 'neutral' };
}

export function napDurationInsight(sleep) {
  const { thisWeek: tw, lastWeek: lw } = splitByWeek(sleep);

  const twDurations = tw
    .filter((i) => !isNightSleep(i))
    .map(sleepDurationMinutes)
    .filter((v) => v != null && v > 0);

  if (twDurations.length < MIN_ENTRIES) return null;

  const thisAvg = mean(twDurations);
  const lwDurations = lw
    .filter((i) => !isNightSleep(i))
    .map(sleepDurationMinutes)
    .filter((v) => v != null && v > 0);
  const lastAvg = lwDurations.length >= MIN_ENTRIES ? mean(lwDurations) : null;
  const trend   = trendOf(thisAvg, lastAvg);

  const body = lastAvg != null
    ? `Average nap ${formatMinutes(thisAvg)} — ${changeLabel(trend)} from ${formatMinutes(lastAvg)} last week`
    : `Averaging ${formatMinutes(thisAvg)} per nap this week`;

  return {
    icon: '😴',
    title: 'Nap duration',
    body,
    trend,
    sentiment: trend === 'up' ? 'positive' : trend === 'down' ? 'negative' : 'neutral',
  };
}

export function nightSleepInsight(sleep) {
  const { thisWeek: tw, lastWeek: lw } = splitByWeek(sleep);

  const twDurations = tw
    .filter(isNightSleep)
    .map(sleepDurationMinutes)
    .filter((v) => v != null && v > 0);

  if (twDurations.length < MIN_ENTRIES) return null;

  const thisAvg = mean(twDurations);
  const lwDurations = lw
    .filter(isNightSleep)
    .map(sleepDurationMinutes)
    .filter((v) => v != null && v > 0);
  const lastAvg = lwDurations.length >= MIN_ENTRIES ? mean(lwDurations) : null;
  const trend   = trendOf(thisAvg, lastAvg);

  const body = lastAvg != null
    ? `Avg night sleep ${formatMinutes(thisAvg)} — ${changeLabel(trend)} from ${formatMinutes(lastAvg)} last week`
    : `Averaging ${formatMinutes(thisAvg)} per night this week`;

  return {
    icon: '🌙',
    title: 'Night sleep',
    body,
    trend,
    sentiment: trend === 'up' ? 'positive' : trend === 'down' ? 'negative' : 'neutral',
  };
}

export function longestSleepInsight(sleep) {
  const { thisWeek: tw } = splitByWeek(sleep);
  const durations = tw.map(sleepDurationMinutes).filter((v) => v != null && v > 0);
  if (durations.length < MIN_ENTRIES) return null;

  const longest = Math.max(...durations);
  return {
    icon: '⭐',
    title: 'Longest sleep stretch',
    body: `${formatMinutes(longest)} — longest single sleep session this week`,
    trend: 'neutral',
    sentiment: 'neutral',
  };
}

export function diaperFrequencyInsight(diapers) {
  const { thisWeek: tw, lastWeek: lw } = splitByWeek(diapers);
  if (tw.length < MIN_ENTRIES) return null;

  function perDay(items) {
    const days = {};
    for (const item of items) {
      const d = parseItemDate(item);
      if (!d) continue;
      const key = d.toDateString();
      days[key] = (days[key] || 0) + 1;
    }
    const counts = Object.values(days);
    return counts.length ? mean(counts) : null;
  }

  const thisAvg = perDay(tw);
  if (thisAvg == null) return null;

  const lastAvg = perDay(lw);
  const trend   = trendOf(thisAvg, lastAvg);

  const body = lastAvg != null
    ? `Averaging ${thisAvg.toFixed(1)} nappies/day — ${changeLabel(trend)} from ${lastAvg.toFixed(1)}/day last week`
    : `Averaging ${thisAvg.toFixed(1)} nappies per day this week`;

  return { icon: '🧷', title: 'Nappy frequency', body, trend, sentiment: 'neutral' };
}

/**
 * Counts night feeds (midnight–05:59) per day and returns the weekly average.
 * Fewer night feeds over time indicates progress toward sleeping through.
 */
export function nightFeedsInsight(bottle, breastfeeding) {
  const allFeeds = [...bottle, ...breastfeeding];
  const { thisWeek: tw, lastWeek: lw } = splitByWeek(allFeeds);
  if (tw.length < MIN_ENTRIES) return null;

  function avgNightFeeds(items) {
    const dayNight = {};
    const daysSeen = new Set();
    for (const item of items) {
      const d = parseItemDate(item);
      if (!d) continue;
      const key = d.toDateString();
      daysSeen.add(key);
      if (d.getHours() < 6) {
        dayNight[key] = (dayNight[key] || 0) + 1;
      }
    }
    if (daysSeen.size < 3) return null;
    const total = Object.values(dayNight).reduce((s, v) => s + v, 0);
    return total / daysSeen.size;
  }

  const thisAvg = avgNightFeeds(tw);
  if (thisAvg == null) return null;

  const lastAvg = avgNightFeeds(lw);
  const trend   = trendOf(thisAvg, lastAvg);

  const body = lastAvg != null
    ? `Avg ${thisAvg.toFixed(1)} night feeds (midnight–6am)/night — ${changeLabel(trend)} from ${lastAvg.toFixed(1)} last week`
    : `Avg ${thisAvg.toFixed(1)} night feeds (midnight–6am) per night this week`;

  // Fewer night feeds = positive (progress toward sleeping through)
  return {
    icon: '🌛',
    title: 'Night feeds',
    body,
    trend,
    sentiment: trend === 'down' ? 'positive' : trend === 'up' ? 'negative' : 'neutral',
  };
}

/**
 * Calculates average daily bottle volume in oz.
 * Helps parents confirm intake is on track — especially for formula-fed babies.
 */
export function dailyMilkIntakeInsight(bottle) {
  const { thisWeek: tw, lastWeek: lw } = splitByWeek(bottle);

  function avgOzPerDay(items) {
    const dayTotals = {};
    for (const item of items) {
      const d = parseItemDate(item);
      if (!d || item.amount == null) continue;
      const key = d.toDateString();
      dayTotals[key] = (dayTotals[key] || 0) + parseFloat(item.amount);
    }
    const totals = Object.values(dayTotals);
    return totals.length >= 3 ? mean(totals) : null;
  }

  const thisAvg = avgOzPerDay(tw);
  if (thisAvg == null) return null;

  const lastAvg = avgOzPerDay(lw);
  const trend   = trendOf(thisAvg, lastAvg);

  const body = lastAvg != null
    ? `Avg ${thisAvg.toFixed(1)} oz/day from bottles — ${changeLabel(trend)} from ${lastAvg.toFixed(1)} oz last week`
    : `Avg ${thisAvg.toFixed(1)} oz per day from bottle feeds this week`;

  return {
    icon: '🥛',
    title: 'Daily milk intake',
    body,
    trend,
    sentiment: trend === 'up' ? 'positive' : trend === 'down' ? 'negative' : 'neutral',
  };
}

/**
 * Tracks average number of daytime naps per day.
 * Useful for spotting nap transitions (3→2, 2→1, 1→0).
 * Treated as neutral — fewer naps can be normal development.
 */
export function napsPerDayInsight(sleep) {
  const { thisWeek: tw, lastWeek: lw } = splitByWeek(sleep);

  function avgNapsPerDay(items) {
    const dayNaps  = {};
    const daysSeen = new Set();
    for (const item of items) {
      const d = parseItemDate(item);
      if (!d) continue;
      const key = d.toDateString();
      daysSeen.add(key);
      if (!isNightSleep(item)) {
        dayNaps[key] = (dayNaps[key] || 0) + 1;
      }
    }
    if (daysSeen.size < 3) return null;
    const total = Object.values(dayNaps).reduce((s, v) => s + v, 0);
    return total / daysSeen.size;
  }

  const thisAvg = avgNapsPerDay(tw);
  if (thisAvg == null) return null;

  const lastAvg = avgNapsPerDay(lw);
  const trend   = trendOf(thisAvg, lastAvg);

  const fmt = (v) => v.toFixed(1);
  const body = lastAvg != null
    ? `Averaging ${fmt(thisAvg)} naps/day — ${changeLabel(trend)} from ${fmt(lastAvg)} last week`
    : `Averaging ${fmt(thisAvg)} nap${parseFloat(fmt(thisAvg)) === 1 ? '' : 's'} per day this week`;

  return {
    icon: '💤',
    title: 'Naps per day',
    body,
    trend,
    sentiment: 'neutral', // nap reduction can be normal development, not a red flag
  };
}

/**
 * Tracks dirty (and "both") nappy frequency separately from total nappy count.
 * Parents often worry about this specifically — constipation and diarrhoea concerns.
 */
export function dirtyNappyInsight(diapers) {
  const { thisWeek: tw, lastWeek: lw } = splitByWeek(diapers);

  function avgDirtyPerDay(items) {
    const dayDirty = {};
    const daysSeen = new Set();
    for (const item of items) {
      const d = parseItemDate(item);
      if (!d) continue;
      const key = d.toDateString();
      daysSeen.add(key);
      if (item.type === 'dirty' || item.type === 'both') {
        dayDirty[key] = (dayDirty[key] || 0) + 1;
      }
    }
    if (daysSeen.size < 3) return null;
    const total = Object.values(dayDirty).reduce((s, v) => s + v, 0);
    return total / daysSeen.size;
  }

  const thisAvg = avgDirtyPerDay(tw);
  if (thisAvg == null) return null;

  const lastAvg = avgDirtyPerDay(lw);
  const trend   = trendOf(thisAvg, lastAvg);

  const body = lastAvg != null
    ? `Avg ${thisAvg.toFixed(1)} dirty nappies/day — ${changeLabel(trend)} from ${lastAvg.toFixed(1)} last week`
    : `Avg ${thisAvg.toFixed(1)} dirty nappies per day this week`;

  return {
    icon: '💩',
    title: 'Dirty nappies',
    body,
    trend,
    sentiment: 'neutral',
  };
}

// ── Main export ────────────────────────────────────────────────────────────

/**
 * computeInsights — runs all insight rules and returns the non-null results.
 *
 * @param {{ bottle, breastfeeding, sleep, diapers }} data  14-day arrays from usePatternData
 * @returns {Array<{ icon, title, body, trend, sentiment }>}
 */
export function computeInsights({ bottle = [], breastfeeding = [], sleep = [], diapers = [] }) {
  return [
    feedingIntervalInsight(bottle, breastfeeding),
    dailyFeedCountInsight(bottle, breastfeeding),
    nightFeedsInsight(bottle, breastfeeding),
    dailyMilkIntakeInsight(bottle),
    napDurationInsight(sleep),
    nightSleepInsight(sleep),
    longestSleepInsight(sleep),
    napsPerDayInsight(sleep),
    diaperFrequencyInsight(diapers),
    dirtyNappyInsight(diapers),
  ].filter(Boolean);
}
